// Detail-card hook for the Logistics Routes panel.
// Owns: the currently-selected route's detail fetch (route + ordered
// stops) + the add-stop form state. Sits beside useLogisticsRoutesPanel
// rather than expanding it; matches the ASUS doctrine refactor's split-
// by-concern hook pattern (useManufacturingMaterials /
// useManufacturingReceiveLines / useManufacturingCreateMaterial).
//
// stop_kind recipient policy mirrors the DB CHECK
// logi_stops_recipient_shape_chk + the service-layer guard in
// stopsService.validateRecipientShape, so the user gets short-circuit
// validation before the round-trip:
//   plant_origin    -> both recipient fields must be null
//   dealer_holding  -> recipient_org_id required, recipient_contact_id null
//   end_customer    -> recipient_contact_id required, recipient_org_id null
//   service / other -> both optional, no shape constraint
import { useCallback, useEffect, useState } from "react";
import {
  addLogisticsStop,
  getLogisticsRouteDetail,
  type LogiRoute,
  type LogiStop,
  type LogiStopKind,
} from "@/lib/logistics";

const STOP_KINDS: LogiStopKind[] = [
  "plant_origin",
  "dealer_holding",
  "end_customer",
  "service",
  "other",
];

export function stopKindAllowsOrgRecipient(kind: LogiStopKind): boolean {
  return kind === "dealer_holding" || kind === "service" || kind === "other";
}

export function stopKindAllowsContactRecipient(kind: LogiStopKind): boolean {
  return kind === "end_customer" || kind === "service" || kind === "other";
}

export function stopKindRequiresOrgRecipient(kind: LogiStopKind): boolean {
  return kind === "dealer_holding";
}

export function stopKindRequiresContactRecipient(kind: LogiStopKind): boolean {
  return kind === "end_customer";
}

export function useLogisticsRouteDetail(routeId: string | null, navTick: number) {
  const [detail, setDetail] = useState<{ route: LogiRoute; stops: LogiStop[] } | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add-stop form state. Address is broken into 4 optional structured
  // fields packed into JSONB at submit time so future "rich address
  // picker" iterations can read the same keys.
  const [adding, setAdding] = useState(false);
  const [newStopKind, setNewStopKind] = useState<LogiStopKind>("plant_origin");
  const [newRecipientOrgId, setNewRecipientOrgId] = useState("");
  const [newRecipientContactId, setNewRecipientContactId] = useState("");
  const [newAddressStreet, setNewAddressStreet] = useState("");
  const [newAddressCity, setNewAddressCity] = useState("");
  const [newAddressState, setNewAddressState] = useState("");
  const [newAddressPostalCode, setNewAddressPostalCode] = useState("");
  const [newScheduledStart, setNewScheduledStart] = useState("");
  const [newScheduledEnd, setNewScheduledEnd] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!routeId) {
      setDetail(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await getLogisticsRouteDetail(routeId);
    setLoading(false);
    if (!res.ok) {
      setDetail(null);
      setError(res.error);
      return;
    }
    setDetail({ route: res.data.route, stops: res.data.stops });
  }, [routeId]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  // Clear add-stop form whenever the selected route changes -- the form
  // was for the previous route and shouldn't carry across.
  useEffect(() => {
    setAdding(false);
    setAddError(null);
    setNewStopKind("plant_origin");
    setNewRecipientOrgId("");
    setNewRecipientContactId("");
    setNewAddressStreet("");
    setNewAddressCity("");
    setNewAddressState("");
    setNewAddressPostalCode("");
    setNewScheduledStart("");
    setNewScheduledEnd("");
    setNewNotes("");
  }, [routeId]);

  const startAdd = useCallback(() => {
    setAdding(true);
    setAddError(null);
  }, []);

  const cancelAdd = useCallback(() => {
    setAdding(false);
    setAddError(null);
  }, []);

  const submitAdd = useCallback(async () => {
    if (!routeId) return;

    // Mirror the recipient_shape_chk client-side so the user doesn't
    // wait on a round-trip for an obvious validation failure.
    const orgId = newRecipientOrgId.trim();
    const contactId = newRecipientContactId.trim();
    if (stopKindRequiresOrgRecipient(newStopKind) && !orgId) {
      setAddError("dealer_holding stops require a recipient_org_id (the dealer's org UUID).");
      return;
    }
    if (stopKindRequiresContactRecipient(newStopKind) && !contactId) {
      setAddError("end_customer stops require a recipient_contact_id (the buyer's contact UUID).");
      return;
    }
    if (!stopKindAllowsOrgRecipient(newStopKind) && orgId) {
      setAddError(`${newStopKind} stops do not carry a recipient_org_id; clear that field.`);
      return;
    }
    if (!stopKindAllowsContactRecipient(newStopKind) && contactId) {
      setAddError(`${newStopKind} stops do not carry a recipient_contact_id; clear that field.`);
      return;
    }

    // Pack address from the 4 structured inputs. Empty = {} so the
    // JSONB doesn't carry empty-string clutter.
    const address: Record<string, unknown> = {};
    if (newAddressStreet.trim()) address.street = newAddressStreet.trim();
    if (newAddressCity.trim()) address.city = newAddressCity.trim();
    if (newAddressState.trim()) address.state = newAddressState.trim();
    if (newAddressPostalCode.trim()) address.postal_code = newAddressPostalCode.trim();

    setAddBusy(true);
    setAddError(null);
    const res = await addLogisticsStop({
      route_id: routeId,
      stop_kind: newStopKind,
      recipient_org_id: stopKindAllowsOrgRecipient(newStopKind) && orgId ? orgId : undefined,
      recipient_contact_id:
        stopKindAllowsContactRecipient(newStopKind) && contactId ? contactId : undefined,
      address: Object.keys(address).length > 0 ? address : undefined,
      scheduled_window_start: newScheduledStart.trim() || undefined,
      scheduled_window_end: newScheduledEnd.trim() || undefined,
      notes: newNotes.trim() || undefined,
    });
    setAddBusy(false);
    if (!res.ok) {
      setAddError(res.error);
      return;
    }
    setAdding(false);
    // Reset transient fields after a successful submit so the form is
    // ready for the next stop on the same route (multi-stop entry is the
    // common case -- plant origin, then dealer holding, then customer).
    setNewRecipientOrgId("");
    setNewRecipientContactId("");
    setNewAddressStreet("");
    setNewAddressCity("");
    setNewAddressState("");
    setNewAddressPostalCode("");
    setNewScheduledStart("");
    setNewScheduledEnd("");
    setNewNotes("");
    await reload();
  }, [
    routeId,
    newStopKind,
    newRecipientOrgId,
    newRecipientContactId,
    newAddressStreet,
    newAddressCity,
    newAddressState,
    newAddressPostalCode,
    newScheduledStart,
    newScheduledEnd,
    newNotes,
    reload,
  ]);

  return {
    detail,
    loading,
    error,
    reload,
    adding,
    startAdd,
    cancelAdd,
    addBusy,
    addError,
    submitAdd,
    newStopKind,
    setNewStopKind,
    newRecipientOrgId,
    setNewRecipientOrgId,
    newRecipientContactId,
    setNewRecipientContactId,
    newAddressStreet,
    setNewAddressStreet,
    newAddressCity,
    setNewAddressCity,
    newAddressState,
    setNewAddressState,
    newAddressPostalCode,
    setNewAddressPostalCode,
    newScheduledStart,
    setNewScheduledStart,
    newScheduledEnd,
    setNewScheduledEnd,
    newNotes,
    setNewNotes,
    stopKindOptions: STOP_KINDS,
  };
}
